import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssessment, startAssessment, retakeAssessment, getAssessmentHistory, getSpecificAssessment, downloadAssessmentPDF, getAssessmentSummary } from '../services/api';
import { supabase } from '../lib/supabase';
import { usePermissions } from '../contexts/PermissionContext';
import { useBusinessContext } from '../contexts/BusinessContext';
import AssessmentModal from '../components/AssessmentModal';
import QuestionModal from '../components/QuestionModal';
import StartAssessmentModal from '../components/StartAssessmentModal';
import AssessmentHistoryModal from '../components/AssessmentHistoryModal';
import FilterPanel from '../components/FilterPanel';
import GapAnalysisHeader from '../components/GapAnalysisHeader';
import OverallProgressCard from '../components/OverallProgressCard';
import CategoryCard from '../components/CategoryCard';
import ScoringGuideModal from '../components/ScoringGuideModal';
import ClearDocumentedTransferableModal from '../components/ClearDocumentedTransferableModal';
import { ActivityTypeHelpModal, AssetTypeHelpModal, ImpactTypeHelpModal } from '../components/FilterHelpModals';
import CategoryHelpModal from '../components/CategoryHelpModal';
import AssessmentSummaryModal from '../components/AssessmentSummaryModal';
import GapDistributionChart from '../components/GapDistributionChart';
import ReadinessAttractivenessChart from '../components/ReadinessAttractivenessChart';
import { BUSINESS_CATEGORY_DESCRIPTIONS, BUSINESS_CATEGORY_KEY_MAP } from '../utils/categoryDescriptions';
import useAssessmentFilters from '../hooks/useAssessmentFilters';
import useAssessmentAnimations from '../hooks/useAssessmentAnimations';
import ViewerBanner from '../components/ViewerBanner';
import logger from '../utils/logger';
import './GapAnalysis.css';

const GapAnalysis = () => {
  const navigate = useNavigate();
  const { isViewer } = usePermissions();
  const { getBusinessHeaders } = useBusinessContext();

  // Use assessment filters hook
  const {
    selectedGapFilter,
    setSelectedGapFilter,
    activityTypeFilter,
    setActivityTypeFilter,
    intangibleAssetFilter,
    setIntangibleAssetFilter,
    readinessAttractivenessFilter,
    setReadinessAttractivenessFilter,
    activityTypeMapping,
    intangibleAssetMapping,
    getGapZone,
    getGapZoneInfo,
    getFilteredQuestions: getFilteredQuestionsFromHook,
    hasActiveFilters,
    clearAllFilters
  } = useAssessmentFilters();

  // Use assessment animations hook
  const {
    categoryAnimationKeys,
    overallProgressAnimationKey,
    triggerAllAnimations,
    triggerInitialAnimations,
    triggerCategoryAnimation
  } = useAssessmentAnimations();

  const [assessment, setAssessment] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [hoveredGapZone, setHoveredGapZone] = useState(null);
  const [showScoringGuide, setShowScoringGuide] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [viewingHistoricalAssessment, setViewingHistoricalAssessment] = useState(false);
  const [showFullQuestionText, setShowFullQuestionText] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showActivityTypeHelp, setShowActivityTypeHelp] = useState(false);
  const [showAssetTypeHelp, setShowAssetTypeHelp] = useState(false);
  const [showImpactTypeHelp, setShowImpactTypeHelp] = useState(false);
  const [showScoringGuideHelp, setShowScoringGuideHelp] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [initialQuestionId, setInitialQuestionId] = useState(null);
  const [isChartHovered, setIsChartHovered] = useState(false);
  const [selectedAssessmentTypes, setSelectedAssessmentTypes] = useState(() => {
    // Load from localStorage or default to just business
    const saved = localStorage.getItem('businessAssessmentTypes');
    return saved ? JSON.parse(saved) : ['business'];
  });
  const [showStartModal, setShowStartModal] = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(null); // Stores the category name to show help for

  useEffect(() => {
    loadAssessment();
    loadAllQuestions();
  }, []);

  const loadAssessment = async () => {
    setLoading(true);
    try {
      const result = await getAssessment();

      if (result.success) {
        logger.debug('Business assessment loaded', { assessmentId: result.data.assessment_id || result.data.id });
        // Transform backend response to ensure assessment_id exists
        const transformedData = {
          ...result.data,
          assessment_id: result.data.assessment_id || result.data.id
        };
        setAssessment(transformedData);
        // Trigger animations only on initial load
        triggerInitialAnimations(transformedData.category_scores);
      } else {
        const startResult = await startAssessment();
        if (startResult.success) {
          const newResult = await getAssessment();
          const transformedData = {
            ...newResult.data,
            assessment_id: newResult.data.assessment_id || newResult.data.id
          };
          setAssessment(transformedData);
          // Trigger animations only on initial load
          triggerInitialAnimations(transformedData.category_scores);
        }
      }
    } catch (error) {
      logger.error('Error loading business assessment', { error });
    }
    setLoading(false);
  };

  const loadAllQuestions = async (assessmentTypes = null) => {
    try {
      // When viewing the dashboard, always load ALL assessment types
      // The selectedAssessmentTypes should only filter when taking the assessment
      const types = assessmentTypes || ['business', 'family enterprise'];
      const typesParam = types.join(',');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/assessment/questions?assessment_types=${encodeURIComponent(typesParam)}`;

      // Use getBusinessHeaders() which includes X-Business-Id for viewers
      const headers = getBusinessHeaders();

      const response = await fetch(url, {
        headers: headers
      });

      if (response.ok) {
        const data = await response.json();
        const questions = data.questions || [];
        setAllQuestions(questions);
        logger.debug('Assessment questions loaded', { questionCount: questions.length, assessmentTypes: data.assessment_types });
        return questions;
      }
    } catch (error) {
      logger.error('Error loading assessment questions', { error });
    }
    return [];
  };

  const handleModalSave = async () => {
    await loadAssessment();
  };

  const getAnsweredQuestions = () => {
    if (!assessment || !assessment.responses) return [];
    return assessment.responses;
  };

  const getCategoryQuestions = (categoryKey) => {
    // Map category keys to actual question category names
    const categoryMap = {
      'financial_performance': 'financial_performance',
      'revenue_quality': 'revenue_quality',
      'customer_concentration': 'customer_concentration',
      'management_team': 'management_team',
      'competitive_position': 'competitive_position',
      'growth_potential': 'growth_potential',
      'intellectual_property': 'intellectual_property',
      'legal_compliance': 'legal_compliance',
      'owner_dependency': 'owner_dependency',
      'strategic_positioning': 'strategic_positioning'
    };
    
    const actualCategory = categoryMap[categoryKey] || categoryKey;
    return allQuestions.filter(q => q.category === actualCategory);
  };

  // getGapZone and getGapZoneInfo are now provided by useAssessmentFilters hook

  // Helper function to get gap zone color based on score
  const getScoreColor = (score) => {
    const zone = getGapZone(score);
    const zoneInfo = getGapZoneInfo(zone);
    return zoneInfo ? zoneInfo.color : '#6b7280'; // default to gray if no zone
  };

  // Helper function to get darker text color for score cards
  const getScoreDarkColor = (score) => {
    if (score > 86) return '#8b6f47';      // Dark bronze/tan - No gaps
    if (score > 72) return '#2d5a2f';      // Dark green - Minor gaps
    if (score > 57) return '#2c5282';      // Dark blue - Considerable gaps
    if (score > 43) return '#8b7500';      // Dark yellow - Critical gaps
    if (score > 28) return '#a0622d';      // Dark orange - Very critical gaps
    return '#991b1b';                       // Dark red - Extremely critical
  };

  // Helper function to get light background color for score cards
  const getScoreLightBg = (score) => {
    if (score > 86) return '#f5f0e8';      // Light bronze/tan - No gaps
    if (score > 72) return '#e6f4e7';      // Light green - Minor gaps
    if (score > 57) return '#e3f2fd';      // Light blue - Considerable gaps
    if (score > 43) return '#fef9e7';      // Light yellow - Critical gaps
    if (score > 28) return '#fff3e6';      // Light orange - Very critical gaps
    return '#fee2e2';                       // Light red - Extremely critical
  };

  const getQuestionsByGapZone = () => {
    const answered = getAnsweredQuestions();
    const zones = {
      'no-gaps': [],
      'minor-gaps': [],
      'considerable-gaps': [],
      'critical-gaps': [],
      'very-critical-gaps': [],
      'extremely-critical': [],
      'n-a': []
    };

    answered.forEach(answer => {
      const zone = getGapZone(answer.score);
      if (zones[zone]) {
        zones[zone].push(answer);
      }
    });

    return zones;
  };

  const getAnswer = (questionId) => {
    const answered = getAnsweredQuestions();
    return answered.find(a => a.question_id === questionId);
  };


  const getScoreTextColor = (percent) => {
    if (percent > 86) return 'text-[#8b6f47]';
    if (percent > 72) return 'text-green-700';
    if (percent > 57) return 'text-blue-700';
    if (percent > 43) return 'text-yellow-700';
    if (percent > 28) return 'text-orange-700';
    return 'text-red-700';
  };

  const getScoreBadgeColor = (score) => {
    if (score > 86) return 'bg-[#f5e6d3] text-[#8b6f47] border-[#c49e73]';  // Gold/tan - No gaps
    if (score > 72) return 'bg-green-100 text-green-800 border-green-300';   // Green - Minor gaps
    if (score > 57) return 'bg-blue-100 text-blue-800 border-blue-300';      // Blue - Considerable gaps
    if (score > 43) return 'bg-yellow-100 text-yellow-800 border-yellow-300'; // Yellow - Critical gaps
    if (score > 28) return 'bg-orange-100 text-orange-800 border-orange-300'; // Orange - Very critical
    return 'bg-red-100 text-red-900 border-red-400';                          // Pink - Extremely critical
  };

  const getCategoryBackgroundColor = (score) => {
    if (score > 86) return 'bg-gradient-to-r from-[#d4c1a3] via-[#e8d4bb] to-gray-50';     // Gold/tan - darker and more saturated
    if (score > 72) return 'bg-gradient-to-r from-green-200 via-green-100 to-gray-50';     // Green - more vibrant
    if (score > 57) return 'bg-gradient-to-r from-blue-200 via-blue-100 to-gray-50';       // Blue - more vibrant
    if (score > 43) return 'bg-gradient-to-r from-yellow-200 via-yellow-100 to-gray-50';   // Yellow - more saturated
    if (score > 28) return 'bg-gradient-to-r from-orange-200 via-orange-100 to-gray-50';   // Orange - more saturated
    return 'bg-gradient-to-r from-red-200 via-red-100 to-gray-50';                          // Red - more saturated
  };

  const handleQuestionEdit = (question, answer) => {
    setSelectedQuestion({
      ...question,
      answer_value: answer?.answer_value,
      answer_text: answer?.answer_text,
      comments: answer?.comments,
      id: answer?.id
    });
  };

  const handleQuestionSave = async () => {
    setSelectedQuestion(null);
    await loadAssessment();
  };

  const handleStartAssessment = () => {
    // Show the modal to select assessment types
    setShowStartModal(true);
  };

  const handleConfirmStartAssessment = async () => {
    // Reload questions with selected types; use returned value to avoid stale closure
    const freshQuestions = await loadAllQuestions(selectedAssessmentTypes);

    // Close the start modal
    setShowStartModal(false);

    // Find the first unanswered question
    const answeredQuestionIds = new Set(getAnsweredQuestions().map(q => q.question_id));
    const firstUnanswered = freshQuestions.find(q => !answeredQuestionIds.has(q.question_id));

    if (firstUnanswered) {
      setInitialQuestionId(firstUnanswered.question_id);
    } else {
      setInitialQuestionId(null);
    }

    setShowAssessmentModal(true);
  };

  const toggleAssessmentType = (type) => {
    if (type === 'business') {
      // Business is always included, can't be toggled off
      return;
    }

    setSelectedAssessmentTypes(prev => {
      const newTypes = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];

      // Save to localStorage
      localStorage.setItem('businessAssessmentTypes', JSON.stringify(newTypes));

      return newTypes;
    });
  };

  const handleRetakeAssessment = async () => {
    if (window.confirm('Are you sure you want to start a new assessment? This will create a new assessment while keeping your previous one in history.')) {
      try {
        const result = await retakeAssessment();
        if (result.success) {
          setViewingHistoricalAssessment(false);
          await loadAssessment();
          setShowAssessmentModal(true);
        }
      } catch (error) {
        logger.error('Error retaking assessment', { error });
      }
    }
  };

  const handleViewHistory = async () => {
    try {
      const result = await getAssessmentHistory();
      if (result.success) {
        // Backend returns 'history' not 'assessments'
        const history = result.data.history || [];
        // Add total_questions to each history item for completion calculation
        const historyWithTotals = history.map(h => ({
          ...h,
          assessment_id: h.id, // Backend uses 'id', frontend expects 'assessment_id'
          total_questions: allQuestions.length
        }));
        setAssessmentHistory(historyWithTotals);
        setShowHistoryModal(true);
      }
    } catch (error) {
      logger.error('Error loading assessment history', { error });
    }
  };

  const handleLoadHistoricalAssessment = async (assessmentId) => {
    try {
      const result = await getSpecificAssessment(assessmentId);
      if (result.success) {
        // Transform backend response to match frontend expectations
        const transformedData = {
          ...result.data,
          assessment_id: result.data.id, // Backend uses 'id', frontend expects 'assessment_id'
          category_scores: {
            // Business categories
            financial_performance: result.data.financial_performance_score,
            revenue_quality: result.data.revenue_quality_score,
            customer_concentration: result.data.customer_concentration_score,
            management_team: result.data.management_team_score,
            competitive_position: result.data.competitive_position_score,
            growth_potential: result.data.growth_potential_score,
            intellectual_property: result.data.intellectual_property_score,
            legal_compliance: result.data.legal_compliance_score,
            owner_dependency: result.data.owner_dependency_score,
            strategic_positioning: result.data.strategic_positioning_score,
            // Family enterprise categories
            boundary_management: result.data.boundary_management_score,
            family_council: result.data.family_council_score,
            equity_planning: result.data.equity_planning_score,
            wealth_transfer: result.data.wealth_transfer_score,
            family_governing: result.data.family_governing_score,
            family_conflict: result.data.family_conflict_score,
            values_mission: result.data.values_mission_score,
            succession_planning: result.data.succession_planning_score,
            employment_policies: result.data.employment_policies_score,
            education_development: result.data.education_development_score
          }
        };
        setAssessment(transformedData);
        setViewingHistoricalAssessment(true);
        setShowHistoryModal(false);
        // Trigger animations when loading historical assessment
        triggerAllAnimations(transformedData.category_scores);
      }
    } catch (error) {
      logger.error('Error loading historical assessment', { error, assessmentId });
    }
  };

  const handleBackToCurrent = async () => {
    setViewingHistoricalAssessment(false);
    await loadAssessment();
  };

  const handleDownloadPDF = async () => {
    if (!assessment || !assessment.assessment_id) {
      alert('No assessment available to download');
      return;
    }

    setDownloadingPDF(true);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Get business ID from business context (stored as currentBusinessId)
      const businessId = localStorage.getItem('currentBusinessId');
      if (!businessId) {
        throw new Error('Business ID not found. Please select a business first.');
      }

      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/business-gap-assessment/${businessId}`;

      // Fetch PDF
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-Id': businessId
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get filename from headers or generate default
      const contentDisposition = response.headers.get('Content-Disposition');

      let filename = 'Business_Gap_Assessment.pdf';
      if (contentDisposition) {
        // Try to extract filename from Content-Disposition header
        // Handles both: filename="file.pdf" and filename*=UTF-8''file.pdf
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
          // Remove UTF-8 encoding prefix if present
          filename = filename.replace(/^UTF-8''/, '');
          // Decode URI encoding if present
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use the filename as-is
            console.warn('[Business Gap PDF] Failed to decode filename:', e);
          }
        }
      }


      // Download PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      logger.error('Error downloading Business Gap Assessment PDF', { error, assessmentId: assessment.assessment_id });
      alert('Failed to download PDF report: ' + error.message);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleViewSummary = async () => {
    if (!assessment || !assessment.assessment_id) {
      alert('No assessment available to summarize');
      return;
    }

    setLoadingSummary(true);
    setShowSummaryModal(true);
    try {
      const result = await getAssessmentSummary(assessment.assessment_id);
      if (result.success) {
        setSummaryData(result.data);
      } else {
        alert('Failed to load assessment summary: ' + (result.error || 'Unknown error'));
        setShowSummaryModal(false);
      }
    } catch (error) {
      logger.error('Error loading assessment summary', { error, assessmentId: assessment.assessment_id });
      alert('Failed to load assessment summary');
      setShowSummaryModal(false);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleGapZoneClick = (zone) => {
    const newFilter = selectedGapFilter === zone ? null : zone;
    setSelectedGapFilter(newFilter);

    // Auto-expand ALL categories that have questions in this zone
    if (newFilter) {
      const answered = getAnsweredQuestions();
      const categoriesToExpand = [];

      finalCategoryKeys.forEach(catKey => {
        const categoryQuestions = getCategoryQuestions(catKey);
        const hasMatchingQuestions = categoryQuestions.some(q => {
          const answer = answered.find(a => a.question_id === q.question_id);
          return answer && getGapZone(answer.score) === zone;
        });

        if (hasMatchingQuestions) {
          categoriesToExpand.push(catKey);
        }
      });

      // Expand all matching categories by setting expandedCategory to an array
      setExpandedCategory(categoriesToExpand.length > 0 ? categoriesToExpand : []);
    } else {
      // Collapse all when filter is cleared
      setExpandedCategory([]);
    }
  };

  // Filter mappings and helper functions are now provided by useAssessmentFilters hook

  const toggleCategory = (categoryKey) => {
    // Toggle category in the array - add if not present, remove if present
    setExpandedCategory(prev => {
      if (prev.includes(categoryKey)) {
        // Remove category from array
        return prev.filter(key => key !== categoryKey);
      } else {
        // Add category to array
        return [...prev, categoryKey];
      }
    });

    // Trigger animation only for this specific category
    triggerCategoryAnimation(categoryKey);
  };

  // Category display names mapping (in assessment order)
  const categoryDisplayNames = {
    // Business categories
    'financial_performance': 'Financial Performance & Documentation',
    'revenue_quality': 'Revenue Quality & Predictability',
    'customer_concentration': 'Customer Concentration & Relationships',
    'management_team': 'Management Team & Operations',
    'competitive_position': 'Competitive Position & Market Dynamic',
    'growth_potential': 'Growth Potential & Scalability',
    'intellectual_property': 'Intellectual Property & Proprietary Assets',
    'legal_compliance': 'Legal, Compliance & Risk Management',
    'owner_dependency': 'Owner Dependency & Transferability',
    'strategic_positioning': 'Strategic Positioning & Market Reputation',
    // Family enterprise categories
    'family_governing': 'Family Governance & Decision-Making Structure',
    'succession_planning': 'Succession Planning & Next-Generation Readiness',
    'family_conflict': 'Family Communication & Conflict Resolution',
    'equity_planning': 'Ownership Structure & Equity Planning',
    'employment_policies': 'Family Employment & Compensation Policies',
    'family_council': 'Family Council & Board Effectiveness',
    'wealth_transfer': 'Wealth Transfer & Estate Integration',
    'values_mission': 'Family Values, Mission & Legacy Preservation',
    'education_development': 'Education & Development of Rising Generation',
    'boundary_management': 'Family-Business Boundary Management'
  };

  // Define category order (matches assessment question order)
  const categoryOrder = [
    // Business categories
    'financial_performance',
    'revenue_quality',
    'customer_concentration',
    'management_team',
    'competitive_position',
    'growth_potential',
    'intellectual_property',
    'legal_compliance',
    'owner_dependency',
    'strategic_positioning',
    // Family enterprise categories (in database order)
    'family_governing',
    'succession_planning',
    'family_conflict',
    'equity_planning',
    'employment_policies',
    'family_council',
    'wealth_transfer',
    'values_mission',
    'education_development',
    'boundary_management'
  ];

  // Assessment type groupings for bracket indicators
  const assessmentTypeGroups = {
    'business': {
      categories: ['financial_performance', 'revenue_quality', 'customer_concentration', 'management_team', 'competitive_position', 'growth_potential', 'intellectual_property', 'legal_compliance', 'owner_dependency', 'strategic_positioning'],
      label: 'Business Assessment',
      color: 'bg-blue-100 border-blue-400 text-blue-700'
    },
    'family enterprise': {
      categories: ['family_governing', 'succession_planning', 'family_conflict', 'equity_planning', 'employment_policies', 'family_council', 'wealth_transfer', 'values_mission', 'education_development', 'boundary_management'],
      label: 'Family Enterprise',
      color: 'bg-purple-100 border-purple-400 text-purple-700'
    }
  };

  // Helper function to get assessment type for a category
  const getAssessmentType = (catKey) => {
    for (const [type, group] of Object.entries(assessmentTypeGroups)) {
      if (group.categories.includes(catKey)) {
        return { type, ...group };
      }
    }
    return null;
  };

  // Helper function to check if this is the first category in a group
  const isFirstInGroup = (catKey, allCatKeys) => {
    const assessmentType = getAssessmentType(catKey);
    if (!assessmentType) return false;
    const firstCatInGroup = assessmentType.categories[0];
    return catKey === firstCatInGroup;
  };

  // Helper function to check if this is the last category in a group
  const isLastInGroup = (catKey, allCatKeys) => {
    const assessmentType = getAssessmentType(catKey);
    if (!assessmentType) return false;
    const lastCatInGroup = assessmentType.categories[assessmentType.categories.length - 1];
    return catKey === lastCatInGroup;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading business assessment...</div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Assessment Found</h2>
          {!isViewer && (
            <button
              onClick={async () => {
                await startAssessment();
                loadAssessment();
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Start Assessment
            </button>
          )}
          {isViewer && (
            <p className="text-gray-600 mt-2">
              No assessment data available to view yet.
            </p>
          )}
        </div>
      </div>
    );
  }

  const answeredQuestions = getAnsweredQuestions();
  const progressPercent = allQuestions.length > 0
    ? (assessment.answered_questions / allQuestions.length) * 100
    : 0;

  const categoryScores = assessment.category_scores || {};

  // Calculate scores for each filter category (must be after answeredQuestions is defined)
  const calculateFilterScores = (mapping) => {
    const scores = {};

    Object.keys(mapping).forEach(filterKey => {
      const questionIds = mapping[filterKey];

      // Match questions by question_id (now using string IDs like 'FIN-001', 'REV-002', etc.)
      const matchingResponses = answeredQuestions.filter(q => {
        return questionIds.includes(q.question_id);
      });

      // Calculate average score, excluding N/A (0) responses
      const validResponses = matchingResponses.filter(r => r.answer_value !== 0);
      if (validResponses.length > 0) {
        const totalScore = validResponses.reduce((sum, r) => sum + (r.score || 0), 0);
        scores[filterKey] = totalScore / validResponses.length;
      } else {
        scores[filterKey] = 0;
      }
    });

    return scores;
  };

  const activityTypeScores = calculateFilterScores(activityTypeMapping);
  const intangibleAssetScores = calculateFilterScores(intangibleAssetMapping);
  
  // Frontend fallback: Calculate real category scores if backend returns same score for all
  const calculateCategoryScores = () => {
    const scores = {};
    const answered = getAnsweredQuestions();
    
    categoryOrder.forEach(catKey => {
      const categoryQuestions = allQuestions.filter(q => q.category === catKey);
      const categoryAnswers = answered.filter(a => {
        const question = categoryQuestions.find(q => q.question_id === a.question_id);
        return question && a.answer_value !== 0; // Exclude N/A answers
      });
      
      if (categoryAnswers.length > 0) {
        const totalScore = categoryAnswers.reduce((sum, a) => sum + a.score, 0);
        scores[catKey] = totalScore / categoryAnswers.length;
      } else {
        scores[catKey] = 0;
      }
    });
    
    return scores;
  };
  
  // Check if backend returned all same scores (indicating it's just using overall score)
  const backendScoreValues = Object.values(categoryScores);
  const allSameScore = backendScoreValues.length > 0 && 
    backendScoreValues.every(score => score === backendScoreValues[0]);
  
  // Use calculated scores if backend scores are all the same
  const finalCategoryScores = allSameScore ? calculateCategoryScores() : categoryScores;

  logger.debug('Category score processing', { backendScores: categoryScores, allSameScore, finalScores: finalCategoryScores });

  // Use ordered category list - filter those that exist in scores
  const backendKeys = Object.keys(finalCategoryScores);

  const categoryKeys = categoryOrder.filter(key => {
    return finalCategoryScores.hasOwnProperty(key);
  });

  // If no matches with ordered list, use backend keys directly
  const finalCategoryKeys = categoryKeys.length > 0 ? categoryKeys : backendKeys;

  // Filter questions by category and active filters (using useAssessmentFilters hook)
  const getFilteredQuestions = (categoryKey) => {
    const categoryQuestions = getCategoryQuestions(categoryKey);
    return getFilteredQuestionsFromHook(categoryQuestions, getAnswer);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Viewer Banner */}
      <ViewerBanner />

      {/* Header Section */}
      <GapAnalysisHeader
        viewingHistoricalAssessment={viewingHistoricalAssessment}
        assessment={assessment}
        onBackToCurrent={handleBackToCurrent}
        onViewHistory={handleViewHistory}
        onNavigateToTasks={() => navigate('/task-manager')}
        onDownloadPDF={handleDownloadPDF}
        downloadingPDF={downloadingPDF}
        onRetakeAssessment={handleRetakeAssessment}
        onShowScoringGuide={() => setShowScoringGuide(true)}
        onStartAssessment={handleStartAssessment}
        totalQuestions={allQuestions.length}
        isViewer={isViewer}
      />

      {/* Show Dashboard if assessment has answers */}
      {assessment.answered_questions > 0 && (
        <div className="space-y-6">
          {/* Overall Score Card */}
          <OverallProgressCard
            assessment={assessment}
            totalQuestions={allQuestions.length}
            progressPercent={progressPercent}
            overallProgressAnimationKey={overallProgressAnimationKey}
            viewingHistoricalAssessment={viewingHistoricalAssessment}
            onStartAssessment={handleStartAssessment}
            onViewSummary={handleViewSummary}
          />

          {/* Main Content Grid - Categories Left, Charts Right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
            {/* LEFT SIDE - Category Scores with Expandable Questions */}
            <div className="lg:col-span-3 space-y-3">
              {/* Filter Panel */}
              <FilterPanel
                showFullQuestionText={showFullQuestionText}
                setShowFullQuestionText={setShowFullQuestionText}
                onShowScoringGuideHelp={() => setShowScoringGuideHelp(true)}
                activityTypeFilter={activityTypeFilter}
                setActivityTypeFilter={setActivityTypeFilter}
                intangibleAssetFilter={intangibleAssetFilter}
                setIntangibleAssetFilter={setIntangibleAssetFilter}
                readinessAttractivenessFilter={readinessAttractivenessFilter}
                setReadinessAttractivenessFilter={setReadinessAttractivenessFilter}
                selectedGapFilter={selectedGapFilter}
                setSelectedGapFilter={setSelectedGapFilter}
                onShowActivityTypeHelp={() => setShowActivityTypeHelp(true)}
                onShowAssetTypeHelp={() => setShowAssetTypeHelp(true)}
                onShowImpactTypeHelp={() => setShowImpactTypeHelp(true)}
                activityTypeMapping={activityTypeMapping}
                intangibleAssetMapping={intangibleAssetMapping}
                getGapZoneInfo={getGapZoneInfo}
                setExpandedCategory={setExpandedCategory}
                finalCategoryKeys={finalCategoryKeys}
                getCategoryQuestions={getCategoryQuestions}
              />

              {finalCategoryKeys.map((catKey) => {
                const score = finalCategoryScores[catKey];
                const categoryName = categoryDisplayNames[catKey] || catKey;
                const categoryQuestions = getFilteredQuestions(catKey);
                const isExpanded = Array.isArray(expandedCategory)
                  ? expandedCategory.includes(catKey)
                  : expandedCategory === catKey;

                const assessmentType = getAssessmentType(catKey);
                const isFirst = isFirstInGroup(catKey, finalCategoryKeys);

                return (
                  <CategoryCard
                    key={catKey}
                    categoryKey={catKey}
                    categoryName={categoryName}
                    score={score}
                    questions={categoryQuestions}
                    isExpanded={isExpanded}
                    onToggle={() => toggleCategory(catKey)}
                    onQuestionEdit={handleQuestionEdit}
                    onShowHelp={setShowCategoryHelp}
                    getAnswer={getAnswer}
                    assessmentType={assessmentType}
                    isFirst={isFirst}
                    animationKey={categoryAnimationKeys[catKey] || 0}
                    viewingHistoricalAssessment={viewingHistoricalAssessment}
                    showFullQuestionText={showFullQuestionText}
                    selectedGapFilter={selectedGapFilter}
                    getCategoryBackgroundColor={getCategoryBackgroundColor}
                    getScoreTextColor={getScoreTextColor}
                    getScoreColor={getScoreColor}
                  />
                );
              })}
            </div>

            {/* RIGHT SIDE - Charts */}
            <div className="lg:col-span-2 space-y-6">
              {/* Gap Distribution Pie Chart */}
              <GapDistributionChart
                answeredQuestions={answeredQuestions}
                assessment={assessment}
                hoveredGapZone={hoveredGapZone}
                setHoveredGapZone={setHoveredGapZone}
                selectedGapFilter={selectedGapFilter}
                setSelectedGapFilter={setSelectedGapFilter}
                getQuestionsByGapZone={getQuestionsByGapZone}
                getGapZoneInfo={getGapZoneInfo}
                handleGapZoneClick={handleGapZoneClick}
                finalCategoryKeys={finalCategoryKeys}
                getCategoryQuestions={getCategoryQuestions}
                setExpandedCategory={setExpandedCategory}
              />

              {/* Readiness vs Attractiveness Chart */}
              <ReadinessAttractivenessChart
                assessment={assessment}
                isChartHovered={isChartHovered}
                setIsChartHovered={setIsChartHovered}
                getScoreColor={getScoreColor}
              />
            </div>
          </div>
        </div>
      )}

      {/* Scoring Guide Modal */}
      <ScoringGuideModal
        isOpen={showScoringGuide}
        onClose={() => setShowScoringGuide(false)}
      />

      {/* Help Modal for "Clear, Documented, & Transferable" */}
      <ClearDocumentedTransferableModal
        isOpen={showScoringGuideHelp}
        onClose={() => setShowScoringGuideHelp(false)}
      />

      {/* Assessment Modal */}
      {showAssessmentModal && (
        <AssessmentModal
          assessmentId={assessment.assessment_id}
          allQuestions={allQuestions}
          answeredQuestions={answeredQuestions}
          initialQuestionId={initialQuestionId}
          onClose={() => {
            setShowAssessmentModal(false);
            setInitialQuestionId(null);
            loadAssessment();
          }}
          onSave={handleModalSave}
        />
      )}

      {/* Question Editor Modal */}
      {selectedQuestion && (
        <QuestionModal
          question={selectedQuestion}
          assessmentId={assessment.assessment_id}
          onClose={() => setSelectedQuestion(null)}
          onSave={handleQuestionSave}
          isReadOnly={viewingHistoricalAssessment}
        />
      )}

      {/* Assessment History Modal */}
      <AssessmentHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        assessmentHistory={assessmentHistory}
        currentAssessment={assessment}
        onLoadHistoricalAssessment={handleLoadHistoricalAssessment}
      />

      {/* Activity Type Help Modal */}
      <ActivityTypeHelpModal
        isOpen={showActivityTypeHelp}
        onClose={() => setShowActivityTypeHelp(false)}
        activityTypeMapping={activityTypeMapping}
        activityTypeScores={activityTypeScores}
        answeredQuestions={answeredQuestions}
        getScoreColor={getScoreColor}
        getScoreDarkColor={getScoreDarkColor}
      />

      {/* Asset Type Help Modal */}
      <AssetTypeHelpModal
        isOpen={showAssetTypeHelp}
        onClose={() => setShowAssetTypeHelp(false)}
        intangibleAssetMapping={intangibleAssetMapping}
        intangibleAssetScores={intangibleAssetScores}
        answeredQuestions={answeredQuestions}
        getScoreColor={getScoreColor}
        getScoreDarkColor={getScoreDarkColor}
      />

      {/* Impact Type Help Modal */}
      <ImpactTypeHelpModal
        isOpen={showImpactTypeHelp}
        onClose={() => setShowImpactTypeHelp(false)}
        assessment={assessment}
        getScoreColor={getScoreColor}
        getScoreDarkColor={getScoreDarkColor}
        getScoreLightBg={getScoreLightBg}
      />

      {/* Assessment Summary Modal */}
      <AssessmentSummaryModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        loadingSummary={loadingSummary}
        summaryData={summaryData}
      />

      {/* Start Assessment Modal */}
      <StartAssessmentModal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        onConfirm={handleConfirmStartAssessment}
        selectedAssessmentTypes={selectedAssessmentTypes}
        onToggleAssessmentType={toggleAssessmentType}
      />

      {/* Category Help Modal */}
      <CategoryHelpModal
        categoryKey={showCategoryHelp}
        onClose={() => setShowCategoryHelp(null)}
        categoryKeyMap={BUSINESS_CATEGORY_KEY_MAP}
        categoryDescriptions={BUSINESS_CATEGORY_DESCRIPTIONS}
      />
    </div>
  );
};

export default GapAnalysis;